import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
// Minimal coordinate presence
const CoordinateGrid = () => <div className="absolute inset-0 w-full h-full opacity-[0.015] pointer-events-none" style={{
  backgroundImage: 'linear-gradient(#F5F5F5 0.5px, transparent 0.5px), linear-gradient(90deg, #F5F5F5 0.5px, transparent 0.5px)',
  backgroundSize: '80px 80px'
}} aria-hidden="true" data-id="element-1390" />;
export function DiagnosticsLanding() {
  return <>
      <Helmet data-id="element-1391">
        <title data-id="element-1392">
          Decodes — One-Time Structural Interpretations | Lazy Monk Labs
        </title>
        <meta name="description" content="Decodes are one-time structural interpretations powered by Decoder. See the underlying pattern of a specific domain." data-id="element-1393" />
        <meta name="robots" content="index, follow" data-id="element-1394" />
      </Helmet>

      <div className="min-h-screen bg-[#0A0A0A] text-[#F5F5F5]" data-id="element-1395">
        {/* Navigation */}
        <header data-id="element-1396">
          <nav className="fixed top-0 left-0 right-0 z-50 bg-[#0A0A0A]/95 backdrop-blur-sm border-b border-[#2B2B2B]" data-id="element-1397">
            <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between" data-id="element-1398">
              <Link to="/" className="flex items-center gap-3 hover:opacity-80 transition-opacity" data-id="element-1399">
                <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/jXd6KyuddrgAfqwwkkpUrR/Lazy_Monk_Labs_Logo.png" alt="Lazy Monk Labs Logo" className="h-8 w-auto" width="32" height="32" data-id="element-1400" />
                <span className="text-base font-mono tracking-tight text-gray-400" data-id="element-1401">
                  Lazy Monk Labs
                </span>
              </Link>
              <div className="flex items-center gap-8" data-id="element-1402">
                <Link to="/decodes" className="text-sm text-[#F5F5F5] hover:text-gray-400 transition-colors font-medium" data-id="element-1403">
                  Decodes
                </Link>
                <Link to="/mirrors/work" className="text-sm text-gray-500 hover:text-[#F5F5F5] transition-colors font-medium" data-id="element-1404">
                  Work Mirror
                </Link>
                <Link to="/decoder" className="text-sm text-gray-500 hover:text-[#F5F5F5] transition-colors font-mono" data-id="element-1405">
                  Decoder
                </Link>
                <Link to="/careers" className="text-sm text-gray-500 hover:text-[#F5F5F5] transition-colors font-mono" data-id="element-1406">
                  Careers
                </Link>
                <a href="https://wa.me/919633096997" target="_blank" rel="noopener noreferrer" className="text-sm text-gray-500 hover:text-[#F5F5F5] transition-colors font-mono" data-id="element-1407">
                  Contact
                </a>
              </div>
            </div>
          </nav>
        </header>

        <main className="pt-24 pb-16" data-id="element-1408">
          {/* 1. HERO */}
          <section className="relative py-32 md:py-40 px-6 overflow-hidden" data-id="element-1409">
            <CoordinateGrid data-id="element-1410" />
            <div className="relative z-10 max-w-5xl mx-auto space-y-12" data-id="element-1411">
              <div className="space-y-6" data-id="element-1412">
                <h1 className="text-5xl md:text-7xl font-serif leading-none text-[#F5F5F5]" style={{
                fontFamily: 'Georgia, serif'
              }} data-id="element-1413">
                  Decodes are one-time structural interpretations.
                </h1>

                <p className="text-xl md:text-2xl text-gray-400 font-light leading-relaxed max-w-3xl" data-id="element-1414">
                  A Decode shows you the underlying pattern of a specific domain
                  — using the same engine that powers all Mirrors at Lazy Monk
                  Labs.
                </p>
              </div>

              <div className="pt-8 flex flex-col sm:flex-row gap-4" data-id="element-1415">
                <a href="#available-decodes" className="px-10 py-4 bg-[#F5F5F5] text-[#0A0A0A] text-sm font-medium hover:bg-gray-200 transition-colors text-center" data-id="element-1416">
                  Explore available Decodes
                </a>
                <Link to="/decoder" className="px-10 py-4 border border-[#F5F5F5] text-[#F5F5F5] text-sm font-medium hover:bg-[#F5F5F5] hover:text-[#0A0A0A] transition-colors text-center" data-id="element-1417">
                  Learn about the engine (Decoder)
                </Link>
              </div>
            </div>
          </section>

          {/* 2. WHAT IS A DECODE */}
          <section className="py-24 px-6 border-t border-[#2B2B2B]" data-id="element-1418">
            <div className="max-w-4xl mx-auto" data-id="element-1419">
              <h2 className="text-3xl md:text-4xl font-serif text-[#F5F5F5] mb-12" style={{
              fontFamily: 'Georgia, serif'
            }} data-id="element-1420">
                What is a Decode?
              </h2>

              <div className="space-y-8 text-lg text-gray-400 leading-relaxed" data-id="element-1421">
                <p data-id="element-1422">
                  A Decode is a one-time interpretation of your underlying
                  pattern through a specific lens: self, work, relationships, or
                  time.
                </p>

                <div className="space-y-4" data-id="element-1423">
                  <p data-id="element-1424">It does not tell you what to do.</p>
                  <p data-id="element-1425">It does not give advice.</p>
                  <p data-id="element-1426">It does not try to change you.</p>
                </div>

                <p className="text-[#F5F5F5] pt-6 border-t border-[#2B2B2B]" data-id="element-1427">
                  It shows you the structure you are already running.
                </p>

                <div className="pt-6" data-id="element-1428">
                  <p className="text-sm text-gray-600" data-id="element-1429">
                    Think of it as a structural scan, not a test.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* 3. WHAT DECODES ARE FOR */}
          <section className="py-24 px-6 bg-[#111214] border-t border-[#2B2B2B]" data-id="element-1430">
            <div className="max-w-4xl mx-auto" data-id="element-1431">
              <h2 className="text-3xl md:text-4xl font-serif text-[#F5F5F5] mb-12" style={{
              fontFamily: 'Georgia, serif'
            }} data-id="element-1432">
                When do people use a Decode?
              </h2>

              <div className="space-y-6 text-base text-gray-400" data-id="element-1433">
                <p data-id="element-1434">• "I want to understand my default operating structure."</p>
                <p data-id="element-1435">• "I want to see my pattern at work."</p>
                <p data-id="element-1436">• "I want to understand a relationship dynamic."</p>
                <p data-id="element-1437">• "I want to understand the phase I'm in."</p>
                <p data-id="element-1438">• "I want clarity before a transition or decision."</p>
              </div>
            </div>
          </section>

          {/* 4. WHAT THIS IS NOT */}
          <section className="py-24 px-6 border-t border-[#2B2B2B]" data-id="element-1439">
            <div className="max-w-6xl mx-auto" data-id="element-1440">
              <h2 className="text-3xl md:text-4xl font-serif text-[#F5F5F5] mb-16 text-center" style={{
              fontFamily: 'Georgia, serif'
            }} data-id="element-1441">
                What this is not
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-12" data-id="element-1442">
                <div className="space-y-4 text-center" data-id="element-1443">
                  <h3 className="text-xl font-medium text-[#F5F5F5]" data-id="element-1444">
                    Not Therapy
                  </h3>
                  <p className="text-base text-gray-400 leading-relaxed" data-id="element-1445">
                    This does not work with emotions or stories. It works with
                    structure.
                  </p>
                </div>

                <div className="space-y-4 text-center" data-id="element-1446">
                  <h3 className="text-xl font-medium text-[#F5F5F5]" data-id="element-1447">
                    Not Coaching
                  </h3>
                  <p className="text-base text-gray-400 leading-relaxed" data-id="element-1448">
                    This does not guide, motivate, or suggest.
                  </p>
                </div>

                <div className="space-y-4 text-center" data-id="element-1449">
                  <h3 className="text-xl font-medium text-[#F5F5F5]" data-id="element-1450">
                    Not a Test
                  </h3>
                  <p className="text-base text-gray-400 leading-relaxed" data-id="element-1451">
                    This is not a questionnaire that scores you.
                  </p>
                </div>
              </div>

              <div className="pt-12 text-center" data-id="element-1452">
                <p className="text-lg text-[#F5F5F5]" data-id="element-1453">
                  A Decode is an interpretation, not an instruction.
                </p>
              </div>
            </div>
          </section>

          {/* 5. HOW DECODES ARE MADE */}
          <section className="py-24 px-6 bg-[#111214] border-t border-[#2B2B2B]" data-id="element-1454">
            <div className="max-w-4xl mx-auto" data-id="element-1455">
              <h2 className="text-3xl md:text-4xl font-serif text-[#F5F5F5] mb-12" style={{
              fontFamily: 'Georgia, serif'
            }} data-id="element-1456">
                How a Decode is generated
              </h2>

              <div className="space-y-12" data-id="element-1457">
                {/* Inputs */}
                <div className="space-y-4" data-id="element-1458">
                  <p className="text-sm font-mono text-gray-600 uppercase tracking-wider" data-id="element-1459">
                    Inputs
                  </p>
                  <div className="space-y-3 text-base text-gray-400" data-id="element-1460">
                    <p data-id="element-1461">• Structural anchors (e.g. birth imprint)</p>
                    <p data-id="element-1462">• Or targeted questions (for some decodes)</p>
                  </div>
                </div>

                {/* Arrow */}
                <div className="flex justify-center" data-id="element-1463">
                  <div className="text-gray-600 text-2xl" data-id="element-1464">↓</div>
                </div>

                {/* Decoder */}
                <div className="space-y-4" data-id="element-1465">
                  <p className="text-sm font-mono text-gray-600 uppercase tracking-wider" data-id="element-1466">
                    Decoder
                  </p>
                  <p className="text-base text-gray-400" data-id="element-1467">
                    • Converts signals into Pattern Code
                  </p>
                </div>

                {/* Arrow */}
                <div className="flex justify-center" data-id="element-1468">
                  <div className="text-gray-600 text-2xl" data-id="element-1469">↓</div>
                </div>

                {/* Decode */}
                <div className="space-y-4" data-id="element-1470">
                  <p className="text-sm font-mono text-gray-600 uppercase tracking-wider" data-id="element-1471">
                    Decode
                  </p>
                  <p className="text-base text-gray-400" data-id="element-1472">
                    • A one-time interpretation of that code through a specific
                    lens
                  </p>
                </div>

                {/* Closing line */}
                <div className="pt-8 border-t border-[#2B2B2B]" data-id="element-1473">
                  <p className="text-sm text-gray-600" data-id="element-1474">
                    Different inputs. Same engine.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* 6. AVAILABLE DECODES */}
          <section id="available-decodes" className="py-24 px-6 border-t border-[#2B2B2B]" data-id="element-1475">
            <div className="max-w-6xl mx-auto" data-id="element-1476">
              <h2 className="text-3xl md:text-4xl font-serif text-[#F5F5F5] mb-16" style={{
              fontFamily: 'Georgia, serif'
            }} data-id="element-1477">
                Available Decodes
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-1478">
                {/* Self Decode */}
                <div className="border border-[#2B2B2B] p-8 space-y-6 bg-[#0A0A0A]" data-id="element-1479">
                  <div className="space-y-3" data-id="element-1480">
                    <h3 className="text-xl font-medium text-[#F5F5F5]" data-id="element-1481">
                      Self Decode
                    </h3>
                    <p className="text-base text-gray-400 leading-relaxed" data-id="element-1482">
                      Your core operating structure across situations.
                    </p>
                  </div>
                  <Link to="/start?product=self" className="block w-full px-6 py-3 border border-[#F5F5F5] text-[#F5F5F5] text-center text-sm font-medium hover:bg-[#F5F5F5] hover:text-[#0A0A0A] transition-colors" data-id="element-1483">
                    Run this Decode
                  </Link>
                </div>

                {/* Work Decode */}
                <div className="border border-[#2B2B2B] p-8 space-y-6 bg-[#0A0A0A]" data-id="element-1484">
                  <div className="space-y-3" data-id="element-1485">
                    <h3 className="text-xl font-medium text-[#F5F5F5]" data-id="element-1486">
                      Work Decode
                    </h3>
                    <p className="text-base text-gray-400 leading-relaxed" data-id="element-1487">
                      How you tend to operate around responsibility, decisions,
                      and pressure.
                    </p>
                  </div>
                  <Link to="/start?product=work" className="block w-full px-6 py-3 border border-[#F5F5F5] text-[#F5F5F5] text-center text-sm font-medium hover:bg-[#F5F5F5] hover:text-[#0A0A0A] transition-colors" data-id="element-1488">
                    Run this Decode
                  </Link>
                </div>

                {/* Relationship Decode */}
                <div className="border border-[#2B2B2B] p-8 space-y-6 bg-[#0A0A0A]" data-id="element-1489">
                  <div className="space-y-3" data-id="element-1490">
                    <h3 className="text-xl font-medium text-[#F5F5F5]" data-id="element-1491">
                      Relationship Decode
                    </h3>
                    <p className="text-base text-gray-400 leading-relaxed" data-id="element-1492">
                      Your default pattern in closeness, distance, and conflict.
                    </p>
                  </div>
                  <Link to="/start?product=relationship" className="block w-full px-6 py-3 border border-[#F5F5F5] text-[#F5F5F5] text-center text-sm font-medium hover:bg-[#F5F5F5] hover:text-[#0A0A0A] transition-colors" data-id="element-1493">
                    Run this Decode
                  </Link>
                </div>

                {/* Child Decode */}
                <div className="border border-[#2B2B2B] p-8 space-y-6 bg-[#0A0A0A]" data-id="element-1494">
                  <div className="space-y-3" data-id="element-1495">
                    <h3 className="text-xl font-medium text-[#F5F5F5]" data-id="element-1496">
                      Child Decode
                    </h3>
                    <p className="text-base text-gray-400 leading-relaxed" data-id="element-1497">
                      How a child adapts to novelty, pressure, and boundaries.
                    </p>
                  </div>
                  <Link to="/start?product=child" className="block w-full px-6 py-3 border border-[#F5F5F5] text-[#F5F5F5] text-center text-sm font-medium hover:bg-[#F5F5F5] hover:text-[#0A0A0A] transition-colors" data-id="element-1498">
                    Run this Decode
                  </Link>
                </div>
              </div>
            </div>
          </section>

          {/* 7. HOW THIS RELATES TO MIRRORS */}
          <section className="py-24 px-6 bg-[#111214] border-t border-[#2B2B2B]" data-id="element-1499">
            <div className="max-w-6xl mx-auto" data-id="element-1500">
              <h2 className="text-3xl md:text-4xl font-serif text-[#F5F5F5] mb-16 text-center" style={{
              fontFamily: 'Georgia, serif'
            }} data-id="element-1501">
                Decodes and Mirrors
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-12 mb-12" data-id="element-1502">
                <div className="space-y-4" data-id="element-1503">
                  <h3 className="text-xl font-medium text-[#F5F5F5]" data-id="element-1504">
                    Decodes
                  </h3>
                  <p className="text-base text-gray-400 leading-relaxed" data-id="element-1505">
                    One-time structural interpretations. Good for clarity now.
                  </p>
                </div>

                <div className="space-y-4" data-id="element-1506">
                  <h3 className="text-xl font-medium text-[#F5F5F5]" data-id="element-1507">
                    Mirrors
                  </h3>
                  <p className="text-base text-gray-400 leading-relaxed" data-id="element-1508">
                    Continuous interpretations over time. Good for seeing the
                    pattern in motion.
                  </p>
                </div>
              </div>

              <div className="text-center mb-12" data-id="element-1509">
                <p className="text-lg text-[#F5F5F5]" data-id="element-1510">
                  Same engine. Different time horizons.
                </p>
              </div>

              <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-1511">
                <Link to="/mirrors/work" className="px-10 py-4 border border-[#F5F5F5] text-[#F5F5F5] text-sm font-medium hover:bg-[#F5F5F5] hover:text-[#0A0A0A] transition-colors text-center" data-id="element-1512">
                  Explore Work Mirror
                </Link>
                <Link to="/" className="px-10 py-4 border border-[#F5F5F5] text-[#F5F5F5] text-sm font-medium hover:bg-[#F5F5F5] hover:text-[#0A0A0A] transition-colors text-center" data-id="element-1513">
                  Back to Home
                </Link>
              </div>
            </div>
          </section>

          {/* 8. QUIET FINAL CTA */}
          <section className="py-24 px-6 border-t border-[#2B2B2B]" data-id="element-1514">
            <div className="max-w-4xl mx-auto text-center space-y-12" data-id="element-1515">
              <h2 className="text-3xl md:text-4xl font-serif text-[#F5F5F5]" style={{
              fontFamily: 'Georgia, serif'
            }} data-id="element-1516">
                Choose a Decode
              </h2>

              <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-1517">
                <a href="#available-decodes" className="px-12 py-4 bg-[#F5F5F5] text-[#0A0A0A] text-sm font-medium hover:bg-gray-200 transition-colors" data-id="element-1518">
                  Explore available Decodes
                </a>
                <Link to="/decoder" className="px-12 py-4 border border-[#F5F5F5] text-[#F5F5F5] text-sm font-medium hover:bg-[#F5F5F5] hover:text-[#0A0A0A] transition-colors" data-id="element-1519">
                  Learn about Decoder
                </Link>
              </div>
            </div>
          </section>
        </main>

        {/* Footer */}
        <footer className="py-12 px-6 border-t border-[#2B2B2B]" data-id="element-1520">
          <div className="max-w-6xl mx-auto" data-id="element-1521">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-8" data-id="element-1522">
              <div data-id="element-1523">
                <p className="text-sm text-gray-500 font-mono" data-id="element-1524">
                  Lazy Monk Labs
                </p>
                <p className="text-xs text-gray-600 mt-1" data-id="element-1525">
                  Pattern recognition systems
                </p>
              </div>

              <div className="flex gap-8 text-sm text-gray-500" data-id="element-1526">
                <Link to="/decodes" className="hover:text-[#F5F5F5] transition-colors" data-id="element-1527">
                  Decodes
                </Link>
                <Link to="/mirrors/work" className="hover:text-[#F5F5F5] transition-colors" data-id="element-1528">
                  Work Mirror
                </Link>
                <Link to="/decoder" className="hover:text-[#F5F5F5] transition-colors" data-id="element-1529">
                  Decoder
                </Link>
                <Link to="/careers" className="hover:text-[#F5F5F5] transition-colors" data-id="element-1530">
                  Careers
                </Link>
                <a href="https://wa.me/919633096997" target="_blank" rel="noopener noreferrer" className="hover:text-[#F5F5F5] transition-colors" data-id="element-1531">
                  Contact
                </a>
              </div>

              <div className="text-xs text-gray-600 font-mono" data-id="element-1532">
                © {new Date().getFullYear()}
              </div>
            </div>
          </div>
        </footer>
      </div>
    </>;
}